/**
 * StartingScenarioSystem — Manages starting scenario definitions and application.
 * Scenarios are defined in /src/data/starting_scenarios.json.
 * Each scenario sets the party's initial state (gold, rations, fame, starting quests).
 *
 * @module StartingScenarioSystem
 */

import scenariosData from '../data/starting_scenarios.json';

const StartingScenarioSystem = {
  /**
   * Return all starting scenarios (locked and unlocked).
   * @returns {Array<object>}
   */
  getAll() {
    return scenariosData;
  },

  /**
   * Return only scenarios that are available to the player.
   * @returns {Array<object>}
   */
  getUnlocked() {
    return scenariosData.filter(s => s.unlocked);
  },

  /**
   * Look up a single scenario by id.
   * @param {string} scenarioId
   * @returns {object|null}
   */
  getById(scenarioId) {
    return scenariosData.find(s => s.id === scenarioId) ?? null;
  },

  /**
   * Apply a scenario's starting values to a WorldState-like object.
   * Only fields that exist on worldState are updated, making this safe to call
   * against partial state objects (e.g. during chargen before full world init).
   *
   * @param {string} scenarioId
   * @param {object} worldState — Object with partyGold, rationPoints, etc.
   * @returns {object|false} The matched scenario, or false if not found.
   */
  apply(scenarioId, worldState) {
    const scenario = scenariosData.find(s => s.id === scenarioId);
    if (!scenario) return false;

    if (worldState.partyGold !== undefined) {
      worldState.partyGold = scenario.startingGold;
    }
    if (worldState.rationPoints !== undefined) {
      worldState.rationPoints = scenario.startingRations;
    }
    if (worldState.fame !== undefined) {
      worldState.fame = scenario.startingFame;
    }
    if (worldState.currentRegionLevel !== undefined) {
      worldState.currentRegionLevel = scenario.startingRegionLevel;
    }

    return scenario;
  },
};

export default StartingScenarioSystem;
